import moment from 'jalali-moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie,
  PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import AnalyticsLayout, { DATE_ALL, DateRange, toToman } from './AnalyticsLayout';

const STATUS_LABELS = globalEnum.orderStatus2;

// Brand palette only — blue / sky blue / gray / near-black (no rainbow).
const STATUS_COLORS: Record<string, string> = {
  Sent: '#1A6BFF',
  Created: '#94A3B8',
  Canceled: '#0F1923',
  WaitForCommerceBearingApproval: '#7A8CA0',
  WaitForCommerceFeltBowlApproval: '#64748B',
  WaitForWarehouseApproval: '#4D8FFF',
  InProgress: '#1254D6',
  WarehouseApproved: '#3D4F6B',
  UnapprovedCommerceBearing: '#334155',
  UnapprovedCommerceFeltBowl: '#475569',
  EditedOrder: '#93C5FD',
};

const CHART_COLORS = ['#1A6BFF', '#4D8FFF', '#7A8CA0', '#3D4F6B', '#0F1923', '#1254D6', '#93C5FD', '#64748B'];

export default function Overview() {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<DateRange>({ from: DATE_ALL, to: DATE_ALL });
  const [data, setData] = useState<any>(null);

  const fetchData = useCallback(async () => {
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.overview).get(apiQuery);
    if (res.code === 200) setData(res.data);
    dispatch(setLoading(false));
  }, [dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchData(); }, [fetchData]);

  const statusPieData = data?.ordersByStatus
    ? Object.entries(data.ordersByStatus).map(([k, v]) => ({
        name: STATUS_LABELS[k] || k,
        value: v as number,
        color: STATUS_COLORS[k] || '#94a3b8',
      }))
    : [];

  return (
    <AnalyticsLayout tab="overview" title="خلاصه کلی" dateRange={dateRange} onDateChange={setDateRange}>

      {/* KPI Row */}
      <div className="analyticsKpiRow">
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiGreen">💰</div>
          <div className="analyticsKpiValue">{tools.formatPrice(toToman(data?.totalIncome))} تومان</div>
          <div className="analyticsKpiLabel">مجموع فروش</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiBlue">📦</div>
          <div className="analyticsKpiValue">{data?.totalOrders ?? 0}</div>
          <div className="analyticsKpiLabel">تعداد سفارشات</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiOrange">✅</div>
          <div className="analyticsKpiValue">{data?.ordersByStatus?.Sent ?? 0}</div>
          <div className="analyticsKpiLabel">سفارشات ارسال‌شده</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiPurple">👥</div>
          <div className="analyticsKpiValue">{data?.totalUsers ?? 0}</div>
          <div className="analyticsKpiLabel">تعداد کل کاربران</div>
        </div>
      </div>

      {/* Charts row 1 */}
      <div className="analyticsChartGrid">
        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">درآمد ماهانه (تومان)</div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.monthlyData || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1A6BFF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1A6BFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} width={90} />
              <Tooltip formatter={(v: any) => [tools.formatPrice(toToman(v)) + ' تومان', 'درآمد']} labelStyle={{ fontFamily: 'Yekan Bakh', textAlign: 'right' }} />
              <Area type="monotone" dataKey="income" stroke="#1A6BFF" fill="url(#incomeGrad)" strokeWidth={2} dot={{ r: 4 }} name="درآمد" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">تعداد سفارشات ماهانه</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.monthlyData || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: any) => [v, 'تعداد']} labelStyle={{ fontFamily: 'Yekan Bakh', textAlign: 'right' }} />
              <Bar dataKey="count" fill="#3D4F6B" radius={[6, 6, 0, 0]} name="تعداد سفارشات" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Status pie + legend */}
      {statusPieData.length > 0 && (
        <div className="analyticsChartGrid">
          <div className="analyticsChartCard">
            <div className="analyticsChartTitle">توزیع وضعیت سفارشات</div>
            <div style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 24 }}>
              <ResponsiveContainer width={280} height={260}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" cx="50%" cy="50%" outerRadius={110} innerRadius={50} paddingAngle={2}>
                    {statusPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: any, n: any) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, direction: 'rtl' }}>
                {statusPieData.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                    <span style={{ color: '#374151' }}>{s.name}</span>
                    <span style={{ marginRight: 'auto', fontWeight: 700, color: '#111' }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AnalyticsLayout>
  );
}
